/**
 * 화상통화 진행 상태 + 시그널링 규칙(여러 명 통화). App.tsx에 흩어져 있던 통화 로직을 옮겼다(2026-11-03).
 *
 * ⚠ 런타임 import를 두지 않는다(타입만). 의존성(socket·media·me·notify)은 configure로 주입한다 —
 *   다자간 E2E 하네스(scripts/e2e-call)가 이 파일과 callService를 transpile해 그대로 싣는다.
 *
 * 규칙(서버 services/callSessions.js와 짝)
 *  · 참가자 목록은 서버 이벤트(참가 ack 목록·call:accepted·call:participant-left)만 바꾼다.
 *    피어가 끊겨도 타일을 지우지 않고 '다시 연결 중'으로 둔다 — 예전엔 수락 쪽 목록이 [나, 건 사람]뿐이라
 *    먼저 와 있던 다른 참가자의 스트림이 버려졌고(3명째부터 안 보임), 피어가 끊기면 타일이 영영 사라졌다
 *  · 두 사람 사이에서 offer는 seq가 작은 쪽(먼저 들어온 쪽)만 보낸다. 새로 들어온 쪽은 기다린다
 *  · 끊긴 피어: offer할 쪽은 1.5·3·6·12초 뒤 새 피어로 다시 offer, 받는 쪽은 call:reconnect-request를 보낸다
 *  · 상태는 동기로 들고 있다 — 예전 activeCallRef는 렌더 뒤 effect에서 갱신돼 그 사이 도착한 offer를 버릴 수 있었다
 */
import type { ActiveCall, CallParticipant, CallRecorder, IncomingCallInfo } from '../types';
import type { CallHandlers, SignalMeta } from './callService';
import type { AcquiredMedia, CallMediaState } from '../utils/callMedia';

export interface CallMediaPort {
  setCallId(callId: string): void;
  setHandlers(handlers: CallHandlers): void;
  prepareLocalMedia(): Promise<AcquiredMedia<MediaStreamTrack>>;
  createOfferPeer(userId: string, opts?: { replace?: boolean }): void;
  receiveSignal(fromUserId: string, signal: any, meta?: SignalMeta): void;
  removePeer(userId: string): void;
  resetPeers(): void;
  isPeerConnected(userId: string): boolean;
  hangup(): void;
}

export interface AckResult {
  ok: boolean;
  code?: string;
  [key: string]: any;
}

export interface CallSocketPort {
  emit(event: string, payload: any): void;
  /** 연결이 없거나 시간 안에 답이 없으면 { ok:false, code:'OFFLINE' | 'TIMEOUT' } */
  emitWithAck(event: string, payload: any, timeoutMs?: number): Promise<AckResult>;
}

export interface CallMe {
  userId: string;
  userName: string;
  avatar?: string;
}

export interface CallControllerDeps {
  media: CallMediaPort;
  socket: CallSocketPort;
  getMe(): CallMe | null;
  notify(n: { type: 'info' | 'error'; message: string; duration?: number }): void;
  /** 카메라·마이크를 켠 결과 — 못 켠 것이 있으면 App이 안내한다 */
  onMediaAcquired?(acquired: AcquiredMedia<MediaStreamTrack>): void;
  noAnswerMs?: number;
  connectTimeoutMs?: number;
  reconnectDelaysMs?: number[];
}

export interface CallControllerState {
  call: ActiveCall | null;
  /** 통화 창 대기 화면의 안내(아무도 없을 때 보인다) */
  message: string | null;
}

/** 서버 ack 실패 코드 → 사용자 문구 */
export function describeCallError(code: string | undefined, max?: number): string {
  switch (code) {
    case 'ENDED': return '이미 종료된 통화입니다.';
    case 'FULL': return `통화 인원이 가득 찼습니다(최대 ${max || 6}명).`;
    case 'IN_CALL_ELSEWHERE': return '다른 기기에서 이미 이 통화에 참여하고 있습니다.';
    case 'not_member': return '이 채널의 멤버가 아니어서 통화할 수 없습니다.';
    case 'UNSUPPORTED': return '이 대화방에서는 통화할 수 없습니다.';
    case 'OFFLINE': return '서버에 연결되어 있지 않습니다. 잠시 후 다시 시도해 주세요.';
    case 'TIMEOUT': return '서버가 응답하지 않습니다. 잠시 후 다시 시도해 주세요.';
    default: return '통화를 연결하지 못했습니다. 잠시 후 다시 시도해 주세요.';
  }
}

/** 두 사람 중 누가 offer를 보내는가 — 순번이 작은 쪽. 순번을 모르면(구버전 서버) 아이디로 정한다 */
export function isOfferer(mySeq: number | null | undefined, myId: string,
  otherSeq: number | null | undefined, otherId: string): boolean {
  if (mySeq != null && otherSeq != null && mySeq !== otherSeq) return mySeq < otherSeq;
  return myId < otherId;
}

interface LinkState {
  attempts: number;
  timer: ReturnType<typeof setTimeout> | null;
}

const DEFAULT_RECONNECT_DELAYS = [1500, 3000, 6000, 12000];

function randomKey(): string {
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}

export class CallController {
  private deps: CallControllerDeps | null = null;
  private state: CallControllerState = { call: null, message: null };
  private listeners = new Set<() => void>();
  private mySeq: number | null = null;
  private links = new Map<string, LinkState>();
  private noAnswerTimer: ReturnType<typeof setTimeout> | null = null;
  private messageTimer: ReturnType<typeof setTimeout> | null = null;
  private endTimer: ReturnType<typeof setTimeout> | null = null;
  private hadOthers = false;
  private rejoining = false;
  private preparing = false;
  /** 이 페이지(앱 실행) 하나를 가리킨다 — 소켓이 바뀌어도 같은 기기의 재결합인지 서버가 안다 */
  readonly clientKey = randomKey();

  configure(deps: CallControllerDeps) {
    this.deps = deps;
  }

  // ── 상태 스토어 ───────────────────────────────────────────────────────────
  getState = (): CallControllerState => this.state;

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => { this.listeners.delete(listener); };
  };

  getCall(): ActiveCall | null {
    return this.state.call;
  }

  private set(patch: Partial<CallControllerState>) {
    this.state = { ...this.state, ...patch };
    this.listeners.forEach(l => {
      try { l(); } catch (err) { console.error('[CallController] listener error:', err); }
    });
  }

  private updateCall(fn: (call: ActiveCall) => ActiveCall) {
    if (!this.state.call) return;
    this.set({ call: fn(this.state.call) });
  }

  private updateParticipant(userId: string, patch: Partial<CallParticipant>) {
    this.updateCall(call => ({
      ...call,
      participants: call.participants.map(p => (p.userId === userId ? { ...p, ...patch } : p)),
    }));
  }

  private participant(userId: string): CallParticipant | undefined {
    return this.state.call?.participants.find(p => p.userId === userId);
  }

  private setMessage(message: string | null, clearAfterMs?: number) {
    if (this.messageTimer) { clearTimeout(this.messageTimer); this.messageTimer = null; }
    this.set({ message });
    if (message && clearAfterMs) {
      this.messageTimer = setTimeout(() => {
        this.messageTimer = null;
        if (this.state.message === message) this.set({ message: null });
      }, clearAfterMs);
    }
  }

  private get d(): CallControllerDeps {
    if (!this.deps) throw new Error('callController.configure() 전에 호출됨');
    return this.deps;
  }

  private meId(): string {
    return this.d.getMe()?.userId ?? '';
  }

  private isCurrent(callId: string | undefined): boolean {
    return !!this.state.call && !!callId && this.state.call.callId === callId;
  }

  // ── 미디어·피어 콜백 ──────────────────────────────────────────────────────
  private handlersFor(callId: string): CallHandlers {
    return {
      onSignal: ({ toUserId, signal, pid, toPid }) => {
        if (!this.isCurrent(callId)) return;
        this.d.socket.emit('rtc:signal', {
          callId,
          toUserId,
          signal,
          pid,
          toPid,
          fromUserId: this.meId(), // 구버전 서버 호환 — 지금 서버는 소켓 신원을 쓴다
        });
      },
      onRemoteStream: (userId, stream) => {
        if (!this.isCurrent(callId)) return;
        if (!this.participant(userId)) this.addParticipant({ userId, userName: '참가자' });
        this.updateParticipant(userId, { stream });
      },
      onPeerConnect: (userId) => {
        if (!this.isCurrent(callId)) return;
        this.clearLink(userId);
        this.updateParticipant(userId, { connState: 'connected' });
      },
      onPeerClose: (userId) => {
        if (!this.isCurrent(callId)) return;
        if (!this.participant(userId)) return; // 이미 나간 사람
        this.updateParticipant(userId, { connState: 'reconnecting', stream: undefined });
        this.recover(userId);
      },
      onRemoteMediaState: (userId, state: CallMediaState) => {
        if (!this.isCurrent(callId)) return;
        this.updateParticipant(userId, {
          isMuted: !state.audio,
          isCameraOff: !state.video,
          isSharingScreen: !!state.screen,
        });
      },
      onScreenShareEnded: () => {
        // VideoCallWindow가 구독한다 — 여기서는 상태를 바꿀 것이 없다
        this.screenShareEndedListeners.forEach(l => l());
      },
    };
  }

  private screenShareEndedListeners = new Set<() => void>();
  onScreenShareEnded(listener: () => void): () => void {
    this.screenShareEndedListeners.add(listener);
    return () => { this.screenShareEndedListeners.delete(listener); };
  }

  private async prepareMedia(callId: string) {
    const { media } = this.d;
    media.setCallId(callId);
    media.setHandlers(this.handlersFor(callId));
    const acquired = await media.prepareLocalMedia();
    this.d.onMediaAcquired?.(acquired);
  }

  // ── 연결 유지(offer 방향·재연결) ──────────────────────────────────────────
  private amOffererFor(userId: string): boolean {
    const p = this.participant(userId);
    return isOfferer(this.mySeq, this.meId(), p?.seq, userId);
  }

  private clearLink(userId: string) {
    const l = this.links.get(userId);
    if (l?.timer) clearTimeout(l.timer);
    this.links.delete(userId);
  }

  private link(userId: string): LinkState {
    let l = this.links.get(userId);
    if (!l) {
      l = { attempts: 0, timer: null };
      this.links.set(userId, l);
    }
    return l;
  }

  /** 그 상대와 곧 이어져야 한다 — 시간 안에 이어지지 않으면 recover */
  private watch(userId: string) {
    const l = this.link(userId);
    if (l.timer) clearTimeout(l.timer);
    const callId = this.state.call?.callId;
    l.timer = setTimeout(() => {
      l.timer = null;
      if (!this.isCurrent(callId) || !this.participant(userId)) return;
      if (this.d.media.isPeerConnected(userId)) return;
      this.recover(userId);
    }, this.d.connectTimeoutMs ?? 10000);
  }

  /** 내가 offer할 쪽이면 지금 새 피어로 잇는다 */
  private offerTo(userId: string) {
    this.d.media.createOfferPeer(userId, { replace: true });
    this.watch(userId);
  }

  /** 끊겼거나 이어지지 않는다 — offer할 쪽은 잠시 뒤 다시 offer, 받는 쪽은 상대에게 다시 offer해 달라고 청한다 */
  private recover(userId: string) {
    const l = this.link(userId);
    const delays = this.d.reconnectDelaysMs ?? DEFAULT_RECONNECT_DELAYS;
    if (l.attempts >= delays.length) {
      this.updateParticipant(userId, { connState: 'failed' });
      return;
    }
    const delay = delays[l.attempts];
    l.attempts += 1;
    if (l.timer) clearTimeout(l.timer);
    const callId = this.state.call?.callId;
    l.timer = setTimeout(() => {
      l.timer = null;
      if (!this.isCurrent(callId) || !this.participant(userId)) return;
      if (this.d.media.isPeerConnected(userId)) return;
      this.updateParticipant(userId, { connState: 'reconnecting' });
      if (this.amOffererFor(userId)) {
        this.offerTo(userId);
      } else {
        this.d.socket.emit('call:reconnect-request', { callId, toUserId: userId });
        this.watch(userId);
      }
    }, delay);
  }

  /** 잇지 못한 상대를 다시 시도한다(통화 창의 '다시 연결' 버튼) */
  retry(userId: string) {
    if (!this.participant(userId)) return;
    this.clearLink(userId);
    this.updateParticipant(userId, { connState: 'reconnecting' });
    if (this.amOffererFor(userId)) this.offerTo(userId);
    else {
      this.d.socket.emit('call:reconnect-request', { callId: this.state.call?.callId, toUserId: userId });
      this.watch(userId);
    }
  }

  private addParticipant(p: CallParticipant) {
    this.updateCall(call => (call.participants.some(x => x.userId === p.userId)
      ? call
      : { ...call, participants: [...call.participants, { connState: 'connecting', ...p }] }));
  }

  private removeParticipantLocal(userId: string) {
    this.clearLink(userId);
    this.d.media.removePeer(userId);
    this.updateCall(call => ({ ...call, participants: call.participants.filter(p => p.userId !== userId) }));
  }

  private meParticipant(seq?: number | null): CallParticipant {
    const me = this.d.getMe();
    return {
      userId: me?.userId ?? '',
      userName: me?.userName ?? '',
      avatar: me?.avatar,
      seq: seq ?? undefined,
      connState: 'connected',
    };
  }

  /** 참가 ack(또는 재결합 ack)의 목록으로 참가자를 맞춘다 */
  private applyRoster(res: AckResult) {
    const me = this.meId();
    if (typeof res.mySeq === 'number') this.mySeq = res.mySeq;
    const others: CallParticipant[] = Array.isArray(res.participants)
      ? res.participants
        .filter((p: any) => p && p.userId != null && String(p.userId) !== me)
        .map((p: any) => ({
          userId: String(p.userId),
          userName: String(p.userName || '참가자'),
          avatar: p.avatar || undefined,
          seq: typeof p.seq === 'number' ? p.seq : undefined,
        }))
      : [];
    const keep = new Set(others.map(p => p.userId));
    // 목록에 없는 사람(끊긴 사이 나간 사람)은 지운다
    (this.state.call?.participants || [])
      .filter(p => p.userId !== me && !keep.has(p.userId))
      .forEach(p => this.removeParticipantLocal(p.userId));
    this.updateCall(call => {
      const byId = new Map(call.participants.map(p => [p.userId, p]));
      return {
        ...call,
        callId: res.callId || call.callId,
        initiatorId: res.initiatorId != null ? String(res.initiatorId) : call.initiatorId,
        startedAt: res.startedAt ? new Date(res.startedAt) : call.startedAt,
        channelType: res.channelType || call.channelType,
        maxParticipants: res.maxParticipants || call.maxParticipants,
        participants: [
          { ...(byId.get(me) || this.meParticipant()), seq: this.mySeq ?? undefined },
          ...others.map(o => {
            const prev = byId.get(o.userId);
            return prev ? { ...prev, ...o } : { connState: 'connecting' as const, ...o };
          }),
        ],
      };
    });
    if (others.length > 0) {
      this.hadOthers = true;
      if (this.state.call?.status === 'calling') this.updateCall(call => ({ ...call, status: 'connected' }));
      this.clearNoAnswer();
    }
  }

  private clearNoAnswer() {
    if (this.noAnswerTimer) { clearTimeout(this.noAnswerTimer); this.noAnswerTimer = null; }
  }

  // ── 시작·참가·나가기 ──────────────────────────────────────────────────────
  /**
   * 통화를 건다. 그 방에 이미 진행 중인 통화가 있으면 그 통화에 참가한다(한 방에 통화 하나).
   * channel.id는 실제 방이어야 한다(초안 DM은 호출부가 먼저 만든다)
   */
  async start(channel: { id: number; name?: string; type?: string }): Promise<boolean> {
    if (this.state.call) {
      this.d.notify({ type: 'info', message: '이미 통화 중입니다.' });
      return false;
    }
    const me = this.d.getMe();
    if (!me) return false;
    if (this.preparing) return false; // 권한 창이 떠 있는 동안 두 번 누름
    const callId = `${channel.id}-${Date.now()}`;
    const channelName = channel.name || '통화';
    this.resetSession();
    // ⚠ 미디어를 먼저 켠다 — 통화 창이 마운트될 때 장치 유무(callService.getLocalMediaInfo)를 한 번 읽는다
    this.preparing = true;
    try {
      await this.prepareMedia(callId);
    } finally {
      this.preparing = false;
    }
    if (this.state.call) return false; // preparing이 막으므로 오지 않는다 — 남의 통화 미디어를 건드리지 않는다
    this.set({
      call: {
        channelId: channel.id,
        channelName,
        callId,
        initiatorId: me.userId,
        participants: [this.meParticipant(1)],
        startedAt: new Date(),
        status: 'calling',
        channelType: channel.type,
      },
      message: null,
    });

    const res = await this.d.socket.emitWithAck('call:initiate', {
      channelId: channel.id,
      channelName,
      callId,
      clientKey: this.clientKey,
      // 구버전 서버 호환(지금 서버는 소켓 신원을 쓴다)
      callerId: me.userId,
      callerName: me.userName,
      callerAvatar: me.avatar,
    });
    if (!this.isCurrent(callId)) {
      // 응답을 기다리는 사이 끊었다 — 서버엔 세션이 생겼으니 나가기를 보내 다른 사람 벨을 끈다
      if (res.ok) this.d.socket.emit('call:end', { channelId: channel.id, callId });
      return false;
    }

    if (!res.ok && res.code === 'IN_PROGRESS' && res.callId) {
      // 같은 방에 진행 중인 통화가 있다 — 그 통화에 참가한다(켜 둔 카메라·마이크를 그대로 쓴다)
      this.updateCall(call => ({ ...call, callId: res.callId, channelName: res.channelName || call.channelName, status: 'connected' }));
      this.d.media.setCallId(res.callId);
      this.d.media.setHandlers(this.handlersFor(res.callId));
      return this.sendJoin(res.callId);
    }
    if (!res.ok && res.code !== 'TIMEOUT') {
      this.finishLocal();
      this.d.notify({ type: 'error', message: describeCallError(res.code) });
      return false;
    }
    if (res.ok) this.applyRoster(res);
    // TIMEOUT: 서버가 늦을 뿐 세션은 만들어졌을 수 있다 — 무응답 타이머가 정리한다

    this.noAnswerTimer = setTimeout(() => {
      this.noAnswerTimer = null;
      if (!this.isCurrent(callId) || this.state.call?.status !== 'calling') return;
      this.setMessage('상대방이 응답하지 않습니다.');
      this.endTimer = setTimeout(() => {
        this.endTimer = null;
        if (this.isCurrent(callId) && this.state.call?.status === 'calling') this.hangup();
      }, 2500);
    }, this.d.noAnswerMs ?? 30000);
    return true;
  }

  /** 수신 벨에서 받기 */
  accept(incoming: IncomingCallInfo): Promise<boolean> {
    return this.join(incoming.channelId, incoming.callId, incoming.channelName, {
      callerId: incoming.callerId,
      callerName: incoming.callerName,
      callerAvatar: incoming.callerAvatar,
      channelType: incoming.channelType,
    });
  }

  /** 진행 중인 통화에 참가('통화 중 · 참여' 바 · 수신 벨 공용) */
  async join(channelId: number, callId: string, channelName: string, hint: {
    callerId?: string; callerName?: string; callerAvatar?: string; channelType?: string;
  } = {}): Promise<boolean> {
    if (this.state.call) {
      if (this.state.call.callId === callId) return true;
      this.d.notify({ type: 'info', message: '이미 통화 중입니다.' });
      return false;
    }
    if (!this.d.getMe()) return false;
    if (this.preparing) return false;
    this.resetSession();
    // 장치가 없어도 참가한다(보기 전용) — 예전엔 수락 쪽에서 실패하면 endCall까지 보내 건 사람의 통화도 끝냈다.
    // ⚠ 미디어를 먼저 켠다(통화 창이 마운트 시 장치 유무를 읽는다)
    this.preparing = true;
    try {
      await this.prepareMedia(callId);
    } finally {
      this.preparing = false;
    }
    if (this.state.call) return this.state.call.callId === callId;
    const participants: CallParticipant[] = [this.meParticipant()];
    if (hint.callerId && hint.callerId !== this.meId()) {
      // 참가 ack가 오기 전까지 건 사람을 먼저 보여 준다(ack 목록으로 곧 바뀐다)
      participants.push({ userId: hint.callerId, userName: hint.callerName || '참가자', avatar: hint.callerAvatar, connState: 'connecting' });
    }
    this.set({
      call: {
        channelId,
        channelName: channelName || '통화',
        callId,
        initiatorId: hint.callerId || '',
        participants,
        startedAt: new Date(),
        status: 'connected',
        channelType: hint.channelType,
      },
      message: null,
    });
    return this.sendJoin(callId);
  }

  private async sendJoin(callId: string): Promise<boolean> {
    const me = this.d.getMe();
    const call = this.state.call;
    if (!me || !call) return false;
    const res = await this.d.socket.emitWithAck('call:accept', {
      channelId: call.channelId,
      callId,
      clientKey: this.clientKey,
      // 구버전 서버 호환
      userId: me.userId,
      userName: me.userName,
      avatar: me.avatar,
    });
    if (!this.isCurrent(callId)) {
      // 응답을 기다리는 사이 끊었다 — 서버엔 자리가 생겼으니 나가기를 보낸다
      if (res.ok) this.d.socket.emit('call:end', { channelId: call.channelId, callId });
      return false;
    }
    if (!res.ok) {
      if (res.code !== 'TIMEOUT') {
        this.finishLocal();
      } else {
        // 서버가 늦었다 — 들어간 것인지 모르므로 나가기를 보내 자리를 비운다
        this.d.socket.emit('call:end', { channelId: call.channelId, callId });
        this.finishLocal();
      }
      this.d.notify({ type: 'error', message: describeCallError(res.code, res.max) });
      return false;
    }
    this.applyRoster(res);
    this.updateCall(c => ({
      ...c,
      status: 'connected',
      initialRecorders: Array.isArray(res.recorders) ? (res.recorders as CallRecorder[]) : [],
      initialWhiteboard: res.whiteboard && Array.isArray(res.whiteboard.actions) ? res.whiteboard : undefined,
    }));
    // 먼저 와 있던 사람들이 내게 offer한다(그들의 순번이 작다). 시간 안에 안 오면 다시 청한다
    this.state.call?.participants.forEach(p => {
      if (p.userId === me.userId) return;
      if (this.amOffererFor(p.userId)) this.offerTo(p.userId); // 순번을 모르는 옛 서버 등 — 규칙대로
      else this.watch(p.userId);
    });
    return true;
  }

  /** 나가기(통화 종료 버튼). 1:1은 상대도 끝나고, 그룹은 나만 빠진다 — 서버가 정한다 */
  hangup() {
    const call = this.state.call;
    if (!call) return;
    this.d.socket.emit('call:end', { channelId: call.channelId, callId: call.callId, userId: this.meId() });
    this.finishLocal();
  }

  /** 수신 벨 거절 */
  reject(incoming: IncomingCallInfo) {
    this.d.socket.emit('call:reject', { channelId: incoming.channelId, callId: incoming.callId, userId: this.meId() });
  }

  /** 페이지를 떠난다(새로고침·창 닫기) — 유예를 기다리지 않고 바로 나간다 */
  leaveOnUnload() {
    const call = this.state.call;
    if (!call) return;
    this.d.socket.emit('call:end', { channelId: call.channelId, callId: call.callId });
  }

  private resetSession() {
    this.mySeq = null;
    this.hadOthers = false;
    this.links.forEach(l => l.timer && clearTimeout(l.timer));
    this.links.clear();
    this.clearNoAnswer();
    if (this.endTimer) { clearTimeout(this.endTimer); this.endTimer = null; }
  }

  private finishLocal() {
    this.resetSession();
    if (this.messageTimer) { clearTimeout(this.messageTimer); this.messageTimer = null; }
    this.d.media.hangup();
    this.set({ call: null, message: null });
  }

  // ── 서버 이벤트 ───────────────────────────────────────────────────────────
  /** 누가 새로 들어왔다 — 나보다 늦게 왔으니 내가 offer한다 */
  onAccepted(p: { callId: string; userId: string; userName?: string; avatar?: string; seq?: number }) {
    if (!this.isCurrent(p.callId)) return;
    const userId = String(p.userId);
    if (userId === this.meId()) return;
    this.clearNoAnswer();
    if (this.endTimer) { clearTimeout(this.endTimer); this.endTimer = null; }
    const existing = this.participant(userId);
    if (existing) {
      // 같은 사람이 다시 들어왔다(새 순번) — 옛 피어를 버리고 새로 잇는다
      this.clearLink(userId);
      this.updateParticipant(userId, { seq: p.seq, userName: p.userName || existing.userName, avatar: p.avatar ?? existing.avatar, connState: 'connecting', stream: undefined });
    } else {
      this.addParticipant({ userId, userName: p.userName || '참가자', avatar: p.avatar, seq: p.seq });
    }
    this.hadOthers = true;
    this.updateCall(call => ({ ...call, status: 'connected' }));
    if (this.state.message) this.setMessage(null);
    if (this.amOffererFor(userId)) this.offerTo(userId);
    else this.watch(userId); // 순번상 상대가 offer할 쪽(구버전 서버 등) — 기다린다
  }

  onParticipantLeft(p: { callId?: string; userId: string; reason?: string }) {
    const call = this.state.call;
    if (!call || (p.callId && p.callId !== call.callId)) return;
    const userId = String(p.userId);
    if (userId === this.meId()) return;
    const left = this.participant(userId);
    if (!left) return;
    this.removeParticipantLocal(userId);
    const others = (this.state.call?.participants || []).filter(x => x.userId !== this.meId());
    if (others.length === 0 && p.reason !== 'rejoin') {
      if (call.channelType === 'dm') {
        this.setMessage(`${left.userName}님과의 연결이 끊어졌습니다.`);
      } else if (this.hadOthers) {
        this.setMessage('모두 나갔습니다. 10분 안에 아무도 들어오지 않으면 통화가 끝납니다.');
      }
    }
  }

  onRejected(p: { callId: string; userId: string; userName?: string; channelType?: string }) {
    const call = this.state.call;
    if (!call || p.callId !== call.callId) return;
    const name = p.userName || '상대방';
    const isDm = (call.channelType || p.channelType) === 'dm';
    if (isDm) {
      // 1:1 거절은 그대로 끝난다(서버도 끝낸다 — call:ended가 먼저 오면 그쪽이 정리한다)
      this.clearNoAnswer();
      this.setMessage(`${name}님이 통화를 거절했습니다.`);
      const callId = call.callId;
      this.endTimer = setTimeout(() => {
        this.endTimer = null;
        if (this.isCurrent(callId)) this.hangup();
      }, 2500);
    } else {
      // 그룹은 그 사람만 빠진다 — 예전엔 한 명만 거절해도 건 사람 통화가 끝났고 그게 전원을 끊었다
      this.d.notify({ type: 'info', message: `${name}님이 통화를 거절했습니다.` });
    }
  }

  onEnded(p: { callId: string; reason?: string }) {
    const call = this.state.call;
    if (!call || p.callId !== call.callId) return;
    const wasDmDecline = call.channelType === 'dm' && !!this.endTimer;
    this.finishLocal();
    if (wasDmDecline) return; // 거절 안내를 이미 보였다
    if (p.reason === 'alone_timeout') {
      this.d.notify({ type: 'info', message: '혼자 남은 지 10분이 지나 통화를 끝냈습니다.' });
    } else if (p.reason === 'declined' && call.channelType !== 'dm') {
      this.d.notify({ type: 'info', message: '모두 통화를 거절했습니다.' });
    } else if (p.reason === 'no_answer') {
      this.d.notify({ type: 'info', message: '아무도 받지 않아 통화를 끝냈습니다.' });
    }
  }

  onSignal(p: { callId: string; fromUserId: string; signal: any; pid?: string; toPid?: string }) {
    if (!this.isCurrent(p.callId)) return;
    const from = String(p.fromUserId);
    if (from === this.meId()) return;
    if (!this.participant(from)) this.addParticipant({ userId: from, userName: '참가자' });
    this.d.media.receiveSignal(from, p.signal, { pid: p.pid, toPid: p.toPid });
  }

  /** 상대가 다시 offer해 달라고 청했다(그쪽 피어가 끊겼다) */
  onReconnectRequest(p: { callId: string; fromUserId: string }) {
    if (!this.isCurrent(p.callId)) return;
    const from = String(p.fromUserId);
    if (!this.participant(from)) return;
    if (!this.amOffererFor(from)) return; // 규칙상 상대가 offer할 쪽 — 서로 offer하면 충돌한다
    this.clearLink(from);
    this.updateParticipant(from, { connState: 'reconnecting', stream: undefined });
    this.offerTo(from);
  }

  /** 소켓이 다시 붙었다 — 같은 통화 자리에 다시 묶는다 */
  async onSocketReconnected(): Promise<void> {
    const call = this.state.call;
    if (!call || this.rejoining) return;
    this.rejoining = true;
    try {
      let res = await this.d.socket.emitWithAck('call:rejoin', { callId: call.callId, clientKey: this.clientKey });
      if (!this.isCurrent(call.callId)) return;
      if (!res.ok && (res.code === 'TIMEOUT' || res.code === 'OFFLINE')) {
        await new Promise(r => setTimeout(r, 3000));
        if (!this.isCurrent(call.callId)) return;
        res = await this.d.socket.emitWithAck('call:rejoin', { callId: call.callId, clientKey: this.clientKey });
        if (!this.isCurrent(call.callId)) return;
      }
      if (res.ok) {
        // 자리가 남아 있었다 — 끊긴 사이 바뀐 사람을 맞추고, 이어지지 않은 상대만 다시 잇는다
        this.applyRoster(res);
        this.state.call?.participants.forEach(p => {
          if (p.userId === this.meId()) return;
          if (this.d.media.isPeerConnected(p.userId)) return;
          this.clearLink(p.userId);
          if (this.amOffererFor(p.userId)) this.offerTo(p.userId);
          else {
            this.d.socket.emit('call:reconnect-request', { callId: call.callId, toUserId: p.userId });
            this.watch(p.userId);
          }
        });
        return;
      }
      if (res.code === 'NOT_IN_CALL') {
        // 유예가 지나 자리가 없어졌다 — 새로 참가한다(먼저 와 있던 사람들이 다시 offer한다)
        this.d.media.resetPeers();
        this.links.forEach(l => l.timer && clearTimeout(l.timer));
        this.links.clear();
        this.updateCall(c => ({ ...c, participants: c.participants.filter(p => p.userId === this.meId()) }));
        await this.sendJoin(call.callId);
        return;
      }
      this.finishLocal();
      if (res.code === 'ENDED') this.d.notify({ type: 'info', message: '연결이 끊긴 사이 통화가 끝났습니다.' });
      else this.d.notify({ type: 'error', message: describeCallError(res.code) });
    } finally {
      this.rejoining = false;
    }
  }
}

export const callController = new CallController();
